import { Component, inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe, CurrencyPipe } from '@angular/common';
import { LoadingController, ModalController, IonicModule } from '@ionic/angular';
import { NotificationService } from '../../services/notification.service';
import { DeviceService } from '../../services/device.service';
import { FlagStopPassScanPage } from '../flag-stop-pass-scan/flag-stop-pass-scan.page';
import { LocationService } from '../../services/location.service';
import {
  FlagStopJob,
  FlagStopPassScan,
  FlagStopPerform,
  ScheduledFlagStop,
} from '../../types/flag-stop.type';
import { DispatcherService } from '../../services/dispatcher.service';
import { PassDetails, isCashPass, isSiucPass } from '../../types/pass.type';
import { RiderTypePerform } from '../../types/rider-type-perform.type';
import { PassEntryPage } from '../pass-entry/pass-entry.page';
import { TranslateService } from '../../services/translate.service';
import { NativePluginService } from '../../services/plugin/native-plugins.service';
import { AppData } from 'src/app/data';
import { environment } from 'src/environments/environment';
import {
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Features } from 'src/environments/environment-features.type';
import { decimalValidator } from 'src/app/validators/decimal.validator';
import { combineLatest, distinctUntilChanged, startWith, Subject, takeUntil, tap } from 'rxjs';
import { _ScheduledFlagStop, BaseJob, GpsReport, RiderTypeInfo } from 'src/app/types';
import { IncrementalControlComponent } from '../../components/increment-control/increment-control.component';
import { CurrencyComponent } from '../../components/currency/currency.component';
import { FormErrorComponent } from '../../components/form-error/form-error.component';
import { TranslatePipe } from '@ngx-translate/core';

export const FlagStopPerformPageId = 'flagStopPerformPage';

@Component({
  templateUrl: './flag-stop-perform.page.html',
  styleUrls: ['./flag-stop-perform.page.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IonicModule,
    IncrementalControlComponent,
    CurrencyComponent,
    FormErrorComponent,
    CurrencyPipe,
    TranslatePipe,
  ],
})
export class FlagStopPerformPage implements OnDestroy, OnInit {
  private readonly _data = inject(AppData);
  private readonly _deviceService = inject(DeviceService);
  private readonly _dispatcherService = inject(DispatcherService);
  private readonly _loadingCtrl = inject(LoadingController);
  private readonly _locationService = inject(LocationService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _modalController = inject(ModalController);
  private readonly _datePipe = inject(DatePipe);
  private readonly _translateService = inject(TranslateService);
  private readonly _pluginService = inject(NativePluginService);

  public readonly paraPassEnabled: boolean = this._data.paraPassEnabled;
  public readonly rapidPassEnabled: boolean = this._data.rapidPassEnabled;
  public readonly flagStopsEnabled = this._data.route.flagStopsEnabled;

  public riderTypePerforms: RiderTypePerform[] = [];
  public showExpectedFare = false;

  // the actual value of the stop
  @Input() public scheduledFlagStop: ScheduledFlagStop = <ScheduledFlagStop>{};
  // the route event, just needed to arrive the jobs
  @Input() public _scheduledFlagStop: _ScheduledFlagStop;

  public readonly form = new FormGroup({
    odometer: new FormControl<number>(this._data.gpsReport?.odometer, [
      Validators.required,
      Validators.max(999_999),
    ]),
    types: new FormGroup({}),
    totalCashCollected: new FormControl<number>(0, [
      Validators.required,
      Validators.min(0),
      Validators.max(10_000),
    ]),
    scannedPasses: new FormControl<FlagStopPassScan[]>([]),
    totalBoarded: new FormControl<number>(0),
    totalDeBoarded: new FormControl<number>(0),
  });

  private readonly gtm = (<any>window).dataLayer || [];
  private readonly destroyed$ = new Subject<boolean>();

  public readonly environment = environment;
  public readonly features: Features = environment.features;
  public readonly wholeNumberError = this._translateService.translate('VALIDATION.isInteger');

  public types = this._handleTypes();
  public totalCashExpected: string = '';
  private startingPassengersOnBoard: number =
    this._data.route.currentNumberOnboard ||
    this._data.route.riderTypes?.reduce((acc, type) => acc + type.currentNumberOnboard, 0) ||
    0;
  public totalPassengersOnBoard: number = this.startingPassengersOnBoard;
  public locationName: string = '';

  constructor() {
    this.gtm.push({
      event: 'custom_event',
      custom_event_name: this.scheduledFlagStop?.id
        ? 'view_perform_scheduled_stop'
        : 'view_perform_flag_stop',
    });
    this._calculateTotalsOnChange().subscribe();
  }

  ngOnInit() {
    this.locationName = (this.scheduledFlagStop as unknown as BaseJob).location?.name;
  }

  /** Increment property value by 1 */
  public increment(formControlName: string): void {
    const control = this.form.get(formControlName);
    if (!control) {
      throw new Error(`Control not found: ${formControlName}`);
    }
    control.patchValue((control.value ?? 0) + 1);
  }

  /** Normalize and Generates list of riders / fare types based on environment configurations */
  private _handleTypes(): {
    label: string
    id: number
    formControlName: string
    fare: number | null
    riderTypePerform?: RiderTypePerform
  }[] {
    switch (environment.features.stopRiderType) {
      case 'riderTypes':
        this.showExpectedFare = true;
        const types = (this._data.route.riderTypes ?? [])
          .filter(type => type.isActive)
          .map(type => {
            const formControlName = type.riderTypeId.toString();
            const control = new FormControl<number>(0, [
              Validators.required,
              Validators.min(0),
              Validators.max(999),
              decimalValidator(0),
            ]);
            this.form.controls.types.addControl(formControlName, control);
            return {
              label: type.riderTypeDescription,
              id: type.riderTypeId,
              formControlName,
              fare: type.defaultFare,
              riderTypePerform: {
                ...type,
                initialNumberOnboard: type.currentNumberOnboard,
                initialPassesOnboard: type.onboardPassIds,
                scannedPasses: [],
                passlessBoardedCount: 0,
                passlessDebordedCount: 0,
                totalPasslessFareCollected: 0,
              },
            };
          })
          .sort((a, b) => (a.label > b.label ? 1 : a.label < b.label ? -1 : 0));
        return types;
      case 'fareTypes':
        return (this._data.fareTypes ?? []).map(type => {
          const formControlName = type.id.toString();
          const control = new FormControl<number>(0, [
            Validators.required,
            Validators.min(0),
            Validators.max(999),
            decimalValidator(0),
          ]);
          this.form.controls.types.addControl(formControlName, control);
          return { label: type.displayValue, id: type.id, formControlName, fare: null };
        });
      default:
        throw new Error(`type not implemented ${environment.features.stopRiderType}`);
    }
  }

  /** listens to form changes and recalculates expected cash and onboard totals */
  private _calculateTotalsOnChange() {
    const _typesControl = this.form.controls.types,
      _totalDeBoardedControl = this.form.controls.totalDeBoarded;
    return combineLatest([
      _typesControl.valueChanges.pipe(startWith(_typesControl.value), distinctUntilChanged((pre, current) => JSON.stringify(pre ?? {}) === JSON.stringify(current ?? {}))),
      _totalDeBoardedControl.valueChanges.pipe(startWith(_totalDeBoardedControl.value), distinctUntilChanged()),
      this.form.controls.scannedPasses.valueChanges.pipe(startWith([])),
    ]).pipe(
      tap(([types, totalDeboarded]) => {
        const { expectedFare, totalBoarded } = Object.keys(types).reduce((acc, typeIdString) => {
          const value = types[typeIdString];
          const _type = this.types.find(t => t.id === Number(typeIdString));
          const _cashToCollect = (_type?.fare ?? 0) * value;
          return {
            expectedFare: acc.expectedFare + _cashToCollect,
            totalBoarded: acc.totalBoarded + value,
          };
        }, { expectedFare: 0, totalBoarded: 0 });
        const valueFromPasses = (this.form.controls.scannedPasses.value ?? []).reduce((_acc, pass) => {
          const _passTypeFare = this.types.find(t => t.id === Number(pass.riderTypeId))?.fare ?? 0;
          return _acc + _passTypeFare;
        }, 0) ?? 0;
        this.form.controls.totalBoarded.patchValue(totalBoarded);
        this.totalPassengersOnBoard = (this.startingPassengersOnBoard ?? 0) + totalBoarded - totalDeboarded;
        this.totalCashExpected = Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(expectedFare - valueFromPasses);
      }),
      takeUntil(this.destroyed$),
    );
  }

  /**
   *  Based on the fare information returned by the API,
   * determines whether we need to show the confirmation dialog to the driver.
   * Example: fare being out of rides or funds or being disabled
   */
  private _driverConfirmationRequired(passInfo: PassDetails, recursive: boolean): boolean {
    return (
      !passInfo.isSufficientForRide || // the pass does not sufficiently cover the ride
      ((passInfo.debitAmoutForRide || 0) > 0 && // there is a fare that needs to be collected
        !recursive) || // and we aren't rapid scanning
      passInfo.isExpired ||
      passInfo.isNotYetEffective ||
      (isSiucPass(passInfo.passType) && // Its a SIUC pass and either
        (passInfo.integrationFailure || // the db is offline
          !passInfo.siucStudentIsActive || // the student is not active
          !passInfo.siucMassTransitEnabled || // not eligible for transit
          !passInfo.siucDawgTagIsValid))
    ); // the student dog tag is not found
  }

  /** dismiss flag-perform dialog */
  public async dismiss(data: any = null): Promise<void> {
    await this._modalController.dismiss(data, data ? 'submit' : 'cancel', FlagStopPerformPageId);
  }

  /** handle onboarding riders using passes */
  public async onboardWithPass(recursive: boolean, useModal: boolean): Promise<void> {
    this.gtm.push({ event: 'custom_event', custom_event_name: 'click_ onboard_pass' });
    const passId = (!this._deviceService.isNativeDevice || useModal)
      ? await this._handlePassViaDialog({ recursive, useModal })
      : await this._handlePassViaScanner({ recursive, useModal });
    if (!passId) { return Promise.resolve(); }

    const loading = await this._loadingCtrl.create({
      spinner: 'crescent',
      message: this._translateService.translate('LABEL.checkingPass'),
      backdropDismiss: false,
    });

    await loading.present();
    const passInfo: PassDetails | null = await this._dispatcherService.getFlagStopPassInformation(passId).catch(async err => {
      console.error(err);
      return null;
    });
    await loading.dismiss(null);
    if (!passInfo) {
      this._notificationService.error(this._translateService.translate('MESSAGE.getFlagStopPassInfoError'), null, true);
      return null;
    }

    const riderTypePerform = this.types.find(
      t => t.riderTypePerform?.riderTypeId === passInfo.riderTypeId,
    );
    let flagStopScan: FlagStopPassScan = {
      passId: passId,
      cashFareCollected: 0,
      isPickup: true,
      riderTypeId: passInfo.riderTypeId,
    };

    const confirmationRequired = this._driverConfirmationRequired(passInfo, recursive);
    if (confirmationRequired) {
      const passInfoModal = await this._modalController.create({
        component: FlagStopPassScanPage,
        cssClass: 'parascope-small-modal',
        backdropDismiss: false,
        componentProps: { passInfo, riderTypePerform, passId },
        id: 'FlagStopPassScanPage',
      });
      await passInfoModal.present();
      const { data, role } = await passInfoModal.onDidDismiss();
      if (role === 'cancel')
        return recursive ? this.onboardWithPass(recursive, useModal) : Promise.resolve();
      flagStopScan = data;
    }

    const riderTypeControl = this.form.controls.types.controls[flagStopScan.riderTypeId];
    riderTypeControl.patchValue(riderTypeControl.value + 1);

    const scannedPassesControl = this.form.controls.scannedPasses;
    scannedPassesControl.patchValue([...scannedPassesControl.value, flagStopScan]);
    new Audio('/assets/sounds/beep.wav')
      .play()
      .catch(err => console.error('Could not play the beep.', err));

    if (!confirmationRequired) {
      const successMessage = this._buildOnboardPassAcceptedWithoutConfirmationMessage(passInfo);
      this._notificationService.info(successMessage, 5000, true);
    }

    if (recursive) {
      return this.onboardWithPass(recursive, useModal);
    }

    return Promise.resolve();
  }

  /** Web implementation for handling passes -- PassEntryPage */
  private async _handlePassViaDialog({
    recursive,
    useModal,
  }: {
    recursive: boolean
    useModal: boolean
  }): Promise<string | null> {
    const passInfoModal = await this._modalController.create({
      component: PassEntryPage,
      cssClass: 'parascope-smaller-modal',
      backdropDismiss: false,
      id: 'passInfoModal',
    });
    await passInfoModal.present();
    const dialog = passInfoModal.shadowRoot.querySelector('[role="dialog"]');
    dialog.setAttribute('aria-label', 'Job Details');
    const { data: passId, role } = await passInfoModal.onDidDismiss();
    return role === 'submit' ? passId : null;
  }

  /** Scan passes using scanner -- PassEntryPage */
  private async _handlePassViaScanner({ recursive, useModal }): Promise<string | null> {
    try {
      const result = await this._pluginService.activateBarcodeScanner(
        this._translateService.translate('LABEL.placeBarcode'),
      );
      return result.cancelled ? null : result.text;
    } catch (error) {
      alert(this._translateService.translate('LABEL.scanningFailed') + ' ' + error);
      return null;
    }
  }

  /** Build confirmation scan confirmation message from pass information */
  private _buildOnboardPassAcceptedWithoutConfirmationMessage(passInfo: PassDetails): string {
    const passText =
      passInfo.assignedRiderName != null && passInfo.assignedRiderName.length > 0
        ? `${this._translateService.translate('LABEL.for')} ${passInfo.assignedRiderName} `
        : '';

    if (isCashPass(passInfo.passType)) {
      const balanceStr = Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
        passInfo.remainingBalance,
      );
      return `
        ${this._translateService.translate('LABEL.pass')} ${passText}
        ${this._translateService.translate('LABEL.passAcceptedBalance')}
        ${balanceStr} ${this._translateService.translate('LABEL.remaining')}.
      `;
    }

    const expDateStr =
      passInfo.expirationDate != null
        ? `${this._translateService.translate('LABEL.expiresOn')} ${this._datePipe.transform(passInfo.expirationDate, 'shortDate')}`
        : this._translateService.translate('LABEL.doesNotExpire');

    return `
      Pass ${passText}
      ${this._translateService.translate('LABEL.acceptedPassHas')}
      ${(passInfo.remainingRides || 0) > 0 ? `${passInfo.remainingRides - 1}` : `${this._translateService.translate('LABEL.unlimited')}`}
      ${this._translateService.translate('LABEL.ridesRemainAfter')}
      ${expDateStr}.
    `;
  }

  async completeFlagStopClicked(): Promise<void> {
    this.gtm.push({
      event: 'custom_event',
      custom_event_name: this.scheduledFlagStop?.id
        ? 'click_complete_scheduled_stop'
        : 'click_complete_flag_stop',
    });

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (environment.features.stopRiderType === 'riderTypes') {
      await this.processRiderTypesFlagStop();
    }

    if (environment.features.stopRiderType === 'fareTypes') {
      await this.processFareTypesFlagStop();
    }

    // return the user to the job list
    await this.dismiss(true);
  }

  async processFareTypesFlagStop(): Promise<void> {
    const formValue = this.form.value;
    const { totalDeBoarded, totalCashCollected, odometer } = formValue;

    if (!!this._scheduledFlagStop) {
      // must be done before perform / perform dateTimeReported is computed
      await this._dispatcherService.arriveJobs([this._scheduledFlagStop]);
    }

    const now = new Date();
    const gpsReport: GpsReport = {
      ...this._data.gpsReport,
      dateTimeReported: now,
      occuranceDate: now,
      odometer: odometer || this._data.gpsReport.odometer,
    };

    const jobs: FlagStopJob[] = Object.keys(formValue.types)
      .map(typeIdString => {
        const value = formValue.types[typeIdString];
        const fareType = this._data.fareTypes.find(ft => ft.id === Number(typeIdString));
        return {
          fareTypeId: fareType?.id,
          onboarded: value,
        } as FlagStopJob;
      })
      .filter(job => job.onboarded > 0);

    const flagStopPerform: FlagStopPerform = {
      gpsReport,
      jobs,
      completedDate: now,
      cashFareCollected: totalCashCollected,
      deboarded: totalDeBoarded ?? 0,
    };

    //  if odometer value was updated save it
    if (this.form.controls.odometer.dirty) {
      await this._locationService.setOdometer(odometer);
    }

    // don't submit an empty perform if no one got on or off
    if ((totalDeBoarded ?? 0) === 0 && jobs.length === 0) {
      this.scheduledFlagStop.performed = true;
      return;
    }

    await this._dispatcherService.performFareTypesFlagStop(flagStopPerform);
    this.scheduledFlagStop.performed = true;
  }

  async processRiderTypesFlagStop(): Promise<void> {
    const { totalDeBoarded, totalCashCollected, odometer, types, scannedPasses } = this.form.value;
    const { updatedRiderTypes, jobs } = this._data.route.riderTypes
      .filter(type => type.isActive)
      .reduce(
        (acc, riderType, index, riderTypes) => {
          const isLast = index === riderTypes.length - 1;
          const { deboardedCount, cashCollected } = acc;
          const _boarded = types[riderType.riderTypeId.toString()] ?? 0;
          const _passes = scannedPasses.filter(pass => pass.riderTypeId === riderType.riderTypeId);
          const _deboarded =
            deboardedCount <= riderType.currentNumberOnboard
              ? deboardedCount
              : riderType.currentNumberOnboard;

          const _expectedCashCollected = riderType.defaultFare * (_boarded - _passes.length);
          const _cashCollected = isLast
            ? cashCollected
            : _expectedCashCollected <= cashCollected
              ? _expectedCashCollected
              : cashCollected;

          const _currentNumberOnboard = riderType.currentNumberOnboard + _boarded - _deboarded;
          const _riderType: RiderTypeInfo = {
            ...riderType,
            currentNumberOnboard: _currentNumberOnboard > 0 ? _currentNumberOnboard : 0,
            onboardPassIds: [...riderType.onboardPassIds, ..._passes.map(p => p.passId)],
          };
          const jobs: FlagStopJob[] = [];
          if (_boarded - _passes.length)
            jobs.push({
              isPickup: true,
              riderTypeId: riderType.riderTypeId,
              units: _boarded - _passes.length,
              cashFareCollected: _cashCollected ?? 0,
            });
          if (_passes.length)
            _passes.forEach(p =>
              jobs.push({
                isPickup: true,
                riderTypeId: riderType.riderTypeId,
                units: 1,
                passId: p.passId,
                cashFareCollected: p.cashFareCollected ?? 0,
              }),
            );
          if (_deboarded) {
            jobs.push({
              isPickup: false,
              riderTypeId: riderType.riderTypeId,
              units: _deboarded,
              cashFareCollected: 0,
            });
          }
          return {
            updatedRiderTypes: [...acc.updatedRiderTypes, _riderType],
            jobs: [...acc.jobs, ...jobs],
            deboardedCount: acc.deboardedCount - _deboarded,
            cashCollected: acc.cashCollected - _cashCollected,
          };
        },
        {
          updatedRiderTypes: <RiderTypeInfo[]>[],
          jobs: <FlagStopJob[]>[],
          deboardedCount: totalDeBoarded,
          cashCollected: totalCashCollected,
        },
      );

    //  if odometer value was updated save it
    if (this.form.controls.odometer.dirty) {
      await this._locationService.setOdometer(odometer);
    }
    await this._dispatcherService.performRiderTypesFlagStop({
      flagStop: this.scheduledFlagStop,
      jobs,
      odometer: this.form.controls.odometer.dirty ? odometer : null,
      updatedRiderTypes,
    });

    //  mark it as complete in the UI
    this.scheduledFlagStop.performed = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
